import React, { Fragment, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button } from "@progress/kendo-react-buttons";
import {
  setFormValue,
  setCurrentStepIndex,
  setSteps,
  resetFileInfo,
  resetDatabaseInfo,
  resetDatabaseTableInfo,
  resetDatasetInfo,
  setPageValue,
  getTablesFromDbConnection,
  getDatasetSchemeFromDbConnection,
  createDatasetFromDbConnection,
  getDatasetSchemefromFile,
  createDatasetFromFile,
  setProcessing,
} from "../../../redux/slices/createdatasetpageslice";
import { Form, FormElement } from "@progress/kendo-react-form";
import { formValidator } from "./createdatasetpageformvalidator";
import { Stepper } from "@progress/kendo-react-layout";
import { addNotification } from "../../../redux/slices/notificationsslice";
import {
  isNullOrEmpty,
  isValidDatasetFieldTitle,
  isValidDatasetName,
  isValidDatasetTitle,
  notificationDurationMiddle,
} from "../../../lib/utils";
import { setCurrentPageIsDirty } from "../../../redux/slices/currentpageslice";
import { useNavigate } from "react-router-dom";
import { apiErrorsHandler, getErrorMessages } from "../../../api/httpclient";
import i18next from "i18next";
import { useTranslation } from "react-i18next";

export const CreateDatasetPageForm = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentTheme = useSelector((state) => state.currentTheme);
  const appearance = useSelector((state) => state.appearance);
  const pageState = useSelector((state) => state.createDatasetPage);
  const createDatasetFrom = pageState.formValues.CreateFrom;
  const updateDirtyState = props.updateDirtyState;
  const currentStepIndex = pageState.currentStepIndex;
  const { t } = useTranslation();

  const steps = pageState.steps;
  const stepPages = pageState.stepPages;
  const stepperOrientation = pageState.stepperOrientation;

  const lastStepIndex = steps.length - 1;
  const isLastStep = lastStepIndex === currentStepIndex;
  const isPreviousStepsValid =
    steps
      .slice(0, currentStepIndex)
      .findIndex((currentStep) => currentStep.isValid === false) === -1;

  const latestFormValuesRef = useRef(pageState.formValues);
  let filenameXD = latestFormValuesRef.current.Filename;
  let fileIdXD = latestFormValuesRef.current.FileId;

  //handle is dirty
  useEffect(() => {
    updateDirtyState();
  }, [pageState.formInitialValues, pageState.formValues]);

  useEffect(() => {
    latestFormValuesRef.current = pageState.formValues;
  }, [pageState.formValues]);

  useEffect(() => {
    if (filenameXD && fileIdXD) {
      validateFileForm(latestFormValuesRef.current); // Pass the latest values without triggering re-renders
    }
  }, [filenameXD, fileIdXD]);

  const validateSelection = async (values) => {
    values.File = null;
    dispatch(setPageValue({ id: "sheets", value: [] }));
    dispatch(resetDatabaseInfo());
    dispatch(resetFileInfo());
    return true;
  };

  const validateFileForm = async (values) => {
    const filename = latestFormValuesRef.current.Filename;
    const fileId = latestFormValuesRef.current.FileId;
    const importData = values.IsCheckedImportData;
    const isDataReal = values.IsDataReal;
    const selectedSheet = values.SelectedSheet;
    const startingRow = values.StartingRow;
    const endingRow = values.EndingRow;

    if (
      filename === "" ||
      filename === undefined ||
      fileId === "" ||
      fileId === undefined
    ) {
      dispatch(
        addNotification({
          text: t("datasets.create.upload_file"),
          type: "error",
          closable: true,
          duration: 3,
          position: "topRight",
        })
      );
      return false;
    }
    if (selectedSheet === "" || selectedSheet === undefined) {
      dispatch(
        addNotification({
          text: t("datasets.create.upload_sheet"),
          type: "error",
          closable: true,
          duration: 3,
          position: "topRight",
        })
      );
      return false;
    }

    if (endingRow !== null && endingRow !== "" && endingRow !== undefined) {
      if (Number(startingRow) > Number(endingRow)) {
        dispatch(
          addNotification({
            text: t("datasets.create.row"),
            type: "error",
            closable: true,
            duration: 3,
            position: "topRight",
          })
        );
        return false;
      }
    }

    try {
      await dispatch(
        getDatasetSchemefromFile(
          latestFormValuesRef.current.FileId,
          selectedSheet,
          importData,
          isDataReal,
          startingRow,
          endingRow,
          t
        )
      );
      return true;
    } catch (error) {
      console.log("Error creating dataset: ", error);
      return false;
    }
  };

  const validateDatabaseForm = async (values) => {
    const DatabaseType = values.DatabaseType;
    const Server = values.Server;
    const Database = values.Database;
    const Username = values.Username;
    const Password = values.Password;

    if (
      DatabaseType === "" ||
      Server === "" ||
      Database === "" ||
      Username === "" ||
      Password === ""
    ) {
      dispatch(
        addNotification({
          text: t("datasets.create.fill"),
          type: "error",
          closable: true,
          duration: 3,
          position: "topRight",
        })
      );
      return false;
    }

    try {
      await dispatch(
        getTablesFromDbConnection(
          DatabaseType,
          Server,
          Database,
          Username,
          Password,
          t
        )
      );
      return true;
    } catch (error) {
      console.log("Error creating dataset: ", error);
      return false;
    }
  };

  const validateTableForm = async (values) => {
    const table = values.Table;
    const DatabaseType = values.DatabaseType;
    const Server = values.Server;
    const Database = values.Database;
    const Username = values.Username;
    const Password = values.Password;

    if (table === 0 || table === "" || table === undefined) {
      dispatch(
        addNotification({
          text: t("datasets.create.select_table"),
          type: "error",
          closable: true,
          duration: 3,
          position: "topRight",
        })
      );
      return false;
    }
    try {
      await dispatch(
        getDatasetSchemeFromDbConnection(
          table,
          DatabaseType,
          Server,
          Database,
          Username,
          Password,
          t
        )
      );
      return true;
    } catch (error) {
      console.log("Error creating dataset: ", error);
      return false;
    }
  };

  const validateDatasetForm = async (values) => {
    const DatasetName = values.DatasetName;
    const DatasetTitle = values.DatasetTitle;

    if (
      DatasetName === "" ||
      DatasetName === undefined ||
      DatasetTitle === "" ||
      DatasetTitle === undefined
    ) {
      dispatch(
        addNotification({
          text: t("datasets.create.fill2"),
          type: "error",
          closable: true,
          duration: 3,
          position: "topRight",
        })
      );
      return false;
    }
    if (!isValidDatasetName(DatasetName)) {
      dispatch(
        addNotification({
          text: t("datasets.create.datasetname"),
          type: "error",
          closable: true,
          duration: 3,
          position: "topRight",
        })
      );
      return false;
    }
    if (!isValidDatasetTitle(DatasetTitle)) {
      dispatch(
        addNotification({
          text: t("datasets.create.datasettitle"),
          type: "error",
          closable: true,
          duration: 3,
          position: "topRight",
        })
      );
      return false;
    }

    values.DatasetFields?.forEach((field) => {
      if (
        !isValidDatasetFieldTitle(field.Title) &&
        isNullOrEmpty(field.Title)
      ) {
        dispatch(
          addNotification({
            text: t("datasets.create.fieldtitle"),
            type: "error",
            closable: true,
            duration: 3,
            position: "topRight",
          })
        );
        return false;
      }
      if (field.DataTypeId === "0") {
        dispatch(
          addNotification({
            text: t("datasets.create.field_datatype"),
            type: "error",
            closable: true,
            duration: 3,
            position: "topRight",
          })
        );
        return false;
      }
    });

    return true;
  };

  const scrollToTop = () => {
    const container = document.getElementById("error-list");
    if (container) {
      container.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const validateReview = async (values) => {
    const fileId = pageState.formValues.FileId;
    const name = values.DatasetName;
    const title = values.DatasetTitle;
    const description = values.DatasetDescription;
    const isAlertBase = values.DatasetIsAlertBase;
    const table = values.Table;
    const DatabaseType = values.DatabaseType;
    const Server = values.Server;
    const Database = values.Database;
    const Username = values.Username;
    const Password = values.Password;
    const recurrsEvery = values.DatasetReccursEvery?.Id || 0;
    const importData =
      values.CreateFrom === "fromfile"
        ? values.IsCheckedImportData
        : values.DatasetImportData;
    const isDataReal = values.IsDataReal;
    const selectedSheet = values.SelectedSheet;
    const startingRow = values.StartingRow;
    const endingRow = values.EndingRow;

    // Perform validation checks
    if (!name || !title) {
      dispatch(
        addNotification({
          text: t("datasets.create.fill3"),
          type: "error",
          closable: true,
          duration: 3,
          position: "topRight",
        })
      );
      scrollToTop();
      return false;
    }

    if (!isValidDatasetName(name)) {
      dispatch(
        addNotification({
          text: t("datasets.create.datasetname"),
          type: "error",
          closable: true,
          duration: 3,
          position: "topRight",
        })
      );
      scrollToTop();
      return false;
    }

    if (!isValidDatasetTitle(title)) {
      dispatch(
        addNotification({
          text: t("datasets.create.datasettitle"),
          type: "error",
          closable: true,
          duration: 3,
          position: "topRight",
        })
      );
      scrollToTop();
      return false;
    }

    try {
      if (values.CreateFrom === "fromdb") {
        if (
          !table ||
          !DatabaseType ||
          !Server ||
          !Database ||
          !Username ||
          !Password
        ) {
          scrollToTop();
          return false;
        }

        return await dispatch(
          createDatasetFromDbConnection(
            name,
            title,
            description,
            isAlertBase,
            table,
            importData,
            recurrsEvery,
            pageState.formValues.DatasetFields,
            DatabaseType,
            Server,
            Database,
            Username,
            Password,
            t
          )
        ).then(
          (response) => {
            if (response.data.Succeeded) {
              return true;
            } else {
              scrollToTop();
              return false;
            }
          },
          (err) => {
            dispatch(setProcessing({ processing: false }));
            dispatch(setPageValue({ id: "error_lst", value: err.data.Errors }));
            scrollToTop();
            return false;
          }
        );
      } else if (values.CreateFrom === "fromfile") {
        if (!fileId || !selectedSheet) {
          dispatch(
            addNotification({
              text: t("datasets.create.select_sheet"),
              type: "error",
              closable: true,
              duration: 3,
              position: "topRight",
            })
          );
          scrollToTop();
          return false;
        }
        if (endingRow && Number(startingRow) > Number(endingRow)) {
          dispatch(
            addNotification({
              text: t("datasets.create.row"),
              type: "error",
              closable: true,
              duration: 3,
              position: "topRight",
            })
          );
          scrollToTop();
          return false;
        }

        return await dispatch(
          createDatasetFromFile(
            fileId,
            name,
            title,
            description,
            isAlertBase,
            importData,
            isDataReal,
            startingRow,
            endingRow,
            selectedSheet,
            pageState.formValues.DatasetFields,
            t
          )
        ).then(
          (response) => {
            if (response.data.Succeeded) {
              return true;
            } else {
              scrollToTop();
              return false;
            }
          },
          (err) => {
            dispatch(setProcessing({ processing: false }));
            dispatch(setPageValue({ id: "error_lst", value: err.data.Errors }));
            scrollToTop();
            return false;
          }
        );
      }
    } catch (error) {
      console.error("Error during dataset creation:", error);
      scrollToTop();
      return false;
    }

    dispatch(setCurrentPageIsDirty(false));
    return true;
  };

  const checkStepValidity = async (values) => {
    let stepIsValid = true;
    if (currentStepIndex === 0) {
      stepIsValid = await validateSelection(values);
    } else if (currentStepIndex === 1 && createDatasetFrom == "fromfile") {
      stepIsValid = await validateFileForm(values);
    } else if (currentStepIndex === 1 && createDatasetFrom == "fromdb") {
      stepIsValid = await validateDatabaseForm(values);
    } else if (currentStepIndex === 2 && createDatasetFrom == "fromdb") {
      stepIsValid = await validateTableForm(values);
    } else if (
      (currentStepIndex === 3 && createDatasetFrom == "fromdb") ||
      (currentStepIndex === 2 && createDatasetFrom == "fromfile")
    ) {
      stepIsValid = await validateDatasetForm(values);
    } else if (
      (currentStepIndex === 4 && createDatasetFrom == "fromdb") ||
      (currentStepIndex === 3 && createDatasetFrom == "fromfile")
    ) {
      stepIsValid = await validateReview(values);
    }
    return stepIsValid;
  };

  const onStepSubmit = React.useCallback(
    async (event) => {
      const { values } = event;

      console.log("values", values);
      // Validate the current step
      const stepIsValid = await checkStepValidity(values);

      // Update steps validity
      const updatedSteps = steps.map((step, index) => ({
        ...step,
        isValid: index === currentStepIndex ? stepIsValid : step.isValid,
      }));
      dispatch(setSteps(updatedSteps));

      if (stepIsValid) {
        dispatch(setFormValue(values)); // Persist updated form values to Redux

        if (currentStepIndex < lastStepIndex) {
          dispatch(setCurrentStepIndex(currentStepIndex + 1));
        } else if (isLastStep) {
          // Replace with actual submission logic
          alert(JSON.stringify(values));
        }
      }
    },
    [steps, currentStepIndex, lastStepIndex, isLastStep, dispatch]
  );

  const onPrevClick = React.useCallback(
    (event) => {
      event.preventDefault();
      if (currentStepIndex === 1 && createDatasetFrom == "fromfile") {
        dispatch(resetFileInfo());
      } else if (currentStepIndex === 1 && createDatasetFrom == "fromdb") {
        dispatch(resetDatabaseInfo());
      } else if (currentStepIndex === 2 && createDatasetFrom == "fromdb") {
        dispatch(resetDatabaseTableInfo());
      } else if (
        (currentStepIndex === 3 && createDatasetFrom == "fromdb") ||
        (currentStepIndex === 2 && createDatasetFrom == "fromfile")
      ) {
        dispatch(resetDatasetInfo());
      }
      dispatch(setCurrentStepIndex(Math.max(currentStepIndex - 1, 0)));
    },
    [currentStepIndex, dispatch]
  );

  return (
    <Fragment>
      <div className="form-wrapper">
        <div className={"stepper-wrapper"}>
          <Stepper
            value={currentStepIndex}
            items={steps}
            orientation={stepperOrientation}
          />
        </div>
        <Form
          validator={formValidator}
          key={pageState.formKey}
          onSubmitClick={onStepSubmit}
          initialValues={pageState.formInitialValues}
          values={pageState.formValues}
          render={(formRenderProps) => {
            const StepComponent = stepPages[currentStepIndex];
            return (
              <FormElement horizontal={false}>
                <StepComponent
                  formValues={pageState.formValues} //{formRenderProps.values}
                  dispatch={dispatch}
                />
                {/* Navigation Buttons */}
                <div className="navigation-buttons">
                  <div>
                    {!isLastStep && (
                      <Button
                        className="btn-proceed"
                        onClick={formRenderProps.onSubmit}
                      >
                        {(currentStepIndex == 4 &&
                          pageState.formValues.CreateFrom == "fromdb") ||
                        (currentStepIndex == 3 &&
                          pageState.formValues.CreateFrom == "fromfile")
                          ? t("datasets.create.createbtn")
                          : t("datasets.create.nextbtn")}
                      </Button>
                    )}
                    {!isLastStep && currentStepIndex !== 0 ? (
                      <Button className="btn-cancel" onClick={onPrevClick}>
                        {t("datasets.create.previousbtn")}
                      </Button>
                    ) : null}
                  </div>
                </div>
              </FormElement>
            );
          }}
        />
      </div>
      <style>
        {`        
                    .datasets-page-form .form-wrapper{
                        width:100%;
                    }
                    .datasets-page-form .form-wrapper .stepper-wrapper{
                      padding:20px;
                        width:100%;
                        border: ${currentTheme.managerBorder};
                        background-color: ${currentTheme.componentBgFaded};
                        border-left:0;
                    }
                    .datasets-page-form .form-wrapper .k-stepper{
                        direction: ${appearance.isRightToLeft ? "rtl" : "ltr"}
                    }
                   .datasets-page-form .form-wrapper .k-stepper .k-step-list {
                      display: flex;
                      justify-content: space-between; /* Pushes first and last steps to edges */
                      width: 100%;
                    }

                    .datasets-page-form .form-wrapper .k-stepper .k-step-list .k-step {
                      flex: 1; /* Ensures middle steps spread out */
                      text-align: center;
                    }

                    // .datasets-page-form .form-wrapper .k-stepper .k-step-list .k-step.k-step-first {
                    //   text-align: left; /* Aligns first step to left */
                    // }

                    // .datasets-page-form .form-wrapper .k-stepper .k-step-list .k-step.k-step-last {
                    //   text-align: right; /* Aligns last step to right */
                    // }

                    .datasets-page-form .form-wrapper .k-stepper .k-step-list .k-step.k-step-first {
                      justify-content: flex-start;
                      text-align: left;
                      margin-right: auto;
                    }

                    .datasets-page-form .form-wrapper .k-stepper .k-step-list .k-step.k-step-last {
                      justify-content: flex-end;
                      text-align: right;
                      margin-left: auto;
                    }

                    .datasets-page-form .k-form
                    {          
                        /*the kendo form which is child of my form.content*/     
                        width: 100%;
                        display: flex;
                        flex: 1;
                        justify-content: space-between;
                        flex-direction:column;
                       
                        border: ${currentTheme.managerBorder};
                        padding:15px;
                        overflow-x:auto;

                    }

                    .datasets-page-form .form-region-1
                    {
                        width: 100%;
                        max-width: 1000px;
                        margin-top: 20px !important;/*to override kendo first child of k-form having marginTop=0*/                     
                    }

                    .datasets-page-form .form-region-1 .form-group-1
                    {
                        display: flex;
                        flex-direction: column;
                        width: 100%;
                    }

                   
                    .datasets-page-form > .content
                    {
                        padding: 0px;
                        background-color: transparent;
                        align-items: flex-start;
                    }

                    .datasets-page-form .form-region-root
                    {
                        background-color: transparent;
                    }
                    
                   
                    .datasets-page-form .form-group-1 .toggle-container
                    {
                        /*the div containing the 3 checks*/
                        justify-content: space-between;
                        border-color: ${currentTheme.componentBorderColor};
                        width: 100%;/*620px;*/
                        max-width: 1000px;
                    }

                    .datasets-page-form input.k-input, .datasets-page-form textarea.k-textarea, 
                    .datasets-page-form .k-dropdownlist .k-input-inner
                    {
                        /*make input controls have a bit more height*/
                        padding: 6px 8px;
                    } 

                    .snippet-popup .k-menu-item:nth-child(2)
                    {
                        padding: 0 !important;
                    }

                    .snippet-popup .k-menu-item:nth-child(1),
                    .snippet-popup .k-menu-item:nth-child(3)
                    {
                        padding-top: 3px;
                        padding-bottom: 3px;
                    }


                    .datasets-page-form .k-form-field textarea
                    {
                        height: 200px !important;
                    }

                    .datasets-page-form .navigation-buttons{
                      display: flex;
                      justify-content: space-between;
                      align-content: center;
                      direction: rtl;
                      margin-top:50px;
                    }
                    .datasets-page-form .navigation-buttons .btn-proceed{
                      padding: 6px 28px;
                      border-radius: 0px;
                      border-color: ${currentTheme.primary};
                      color: #fff;
                      min-width:120px;
                      background-color:  ${currentTheme.primary};
                    }
                    .datasets-page-form .navigation-buttons .btn-cancel{
                      margin-right: 16px;
                      padding: 6px 28px;
                      min-width:120px;
                      border-radius: 0px;
                      border-color: rgba(0, 0, 0, 0.1);
                      color: #424242;
                      background-color: #f5f5f5;
                    }


                    }
                `}
      </style>
    </Fragment>
  );
};
